import { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';

/**
 * Closing a facility ends every open enrolment at it and raises one alert to
 * the main branch - hence the explicit reason prompt rather than a bare
 * confirm. Reopening later does not restore the ended enrolments.
 */
const CloseFacilityModal = ({
  show,
  kind,
  facilityName,
  onHide,
  onConfirm,
  isClosing
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (show) setReason('');
  }, [show]);

  const handleSubmit = e => {
    e.preventDefault();
    onConfirm(reason.trim());
  };

  // The mosque module is presented as "Madrasah" throughout the UI
  const KIND_LABELS = {
    school: 'School',
    mosque: 'Madrasah',
    hospital: 'Hospital',
    orphanage: 'Orphanage'
  };
  const kindLabel =
    KIND_LABELS[kind] || kind.charAt(0).toUpperCase() + kind.slice(1);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-0">Close {kindLabel}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="mb-3">
            You are closing{' '}
            <strong>{facilityName || `this ${kindLabel.toLowerCase()}`}</strong>
            . All open enrolments at this {kindLabel.toLowerCase()} will be
            ended today, and an alert will be raised for the main branch.
          </p>
          <Form.Group>
            <Form.Label className="fs-0 fw-medium">
              Reason for closure (optional)
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder={`e.g. ${kindLabel} shut down permanently in June`}
              autoFocus
            />
            <Form.Text className="text-muted">
              Reopening later will not restore the ended enrolments.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={isClosing}>
            Cancel
          </Button>
          <Button type="submit" variant="danger" disabled={isClosing}>
            {isClosing ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Closing...
              </>
            ) : (
              `Close ${kindLabel}`
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

CloseFacilityModal.propTypes = {
  show: PropTypes.bool.isRequired,
  kind: PropTypes.oneOf(['school', 'mosque', 'hospital', 'orphanage'])
    .isRequired,
  facilityName: PropTypes.string,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isClosing: PropTypes.bool
};

export default CloseFacilityModal;
